import * as React from 'react';
import { styled } from '../theme';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

export interface CarouselWidgetProps {
  content: JSX.Element[];
  animationMs?: number;
}

const DEFAULT_CAROUSEL_ANIMATE_MS = 3000;

export const CarouselWidget: React.FC<CarouselWidgetProps> = (props) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [loaded, setLoaded] = React.useState(false);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      loop: props.content.length > 1,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      }
    },
    [
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        let mouseOver = false;
        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => {
            slider.next();
          }, props.animationMs || DEFAULT_CAROUSEL_ANIMATE_MS);
        }
        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      }
    ]
  );

  const slides = instanceRef.current?.track.details.slides || [];

  return (
    <S.Container>
      <S.SliderContainer ref={sliderRef} className={'keen-slider'}>
        {props.content.map((c, index) => {
          return (
            <S.Slider key={`carousel-${index}`} className={'keen-slider__slide'}>
              {c}
            </S.Slider>
          );
        })}
      </S.SliderContainer>
      {loaded && instanceRef.current && slides.length > 1 && (
        <S.Dots>
          {[...Array(slides.length).keys()].map((idx) => {
            return (
              <S.Dot
                key={idx}
                onClick={() => {
                  instanceRef.current?.moveToIdx(idx);
                }}
                active={currentSlide === idx}
              ></S.Dot>
            );
          })}
        </S.Dots>
      )}
    </S.Container>
  );
};

namespace S {
  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    justify-content: center;
    align-items: center;
  `;

  export const SliderContainer = styled.div``;

  export const Slider = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  export const Dots = styled.div`
    display: flex;
    padding: 10px 0;
    justify-content: center;
  `;

  export const Dot = styled.div<{ active: boolean }>`
    border: none;
    width: 10px;
    height: 10px;
    background: ${(p) => (p.active ? ' #c5c5c5' : '#48484a')};
    border-radius: 50%;
    margin: 0 5px;
    padding: 5px;
    cursor: pointer;

    &:focus {
      outline: none;
    }
  `;
}
